import * as React from 'react';
import { Dispatch, SetStateAction, useMemo, useState } from 'react';

import { BufferBagsStep1 } from './BufferBagsStep1';
import { BufferBagsStep2 } from './BufferBagsStep2';
import { Close } from '../../assets/Close';
import { useGetProducts } from '../../hooks/getProducts';
import { Customer, GetProductsRequestType, Option, Order, Pet } from '../../pages/types';
import {
  generateFullName,
  getCountryFlag,
  hasFreshProduct,
  mapSntApiDate,
  petPlanHasBufferBags,
} from '../../utils';
import { LineInfo } from '../LineInfo';

export type BufferBagsContextData = {
  customer: Customer;
  order: Order;
  petOptions: Option[];
  selectedDog?: Option;
  setSelectedDog: Dispatch<SetStateAction<Option | undefined>>;
  navigateBetweenSteps: () => void;
  onConfirm: () => void;
  scheduledDate: string;
  selectedProducts: Option[];
  setSelectedProducts: Dispatch<SetStateAction<Option[]>>;
};

export const BufferBagsContext = React.createContext<BufferBagsContextData>(
  {} as BufferBagsContextData
);

type BufferBagsModalProps = {
  customer: Customer;
  order: Order;
  onConfirm: () => void;
  onCancel: () => void;
};

export const BufferBagsModal = ({ customer, order, onConfirm, onCancel }: BufferBagsModalProps) => {
  const { pets } = customer;
  const orderPets: Pet[] = useMemo(() => {
    const petNames = order.products.map((product: any) => product.pet_plan.pet_name);
    const uniquePetNames = Array.from(new Set(petNames));
    return uniquePetNames
      .map((petName: string) => pets.find((p: Pet) => p.name === petName))
      .filter((pet): pet is Pet => pet !== undefined);
  }, [order.products, pets]);

  const [selectedDog, setSelectedDog] = useState<Option>();

  const [selectedProducts, setSelectedProducts] = useState<Option[]>([]);

  const { products, isLoading } = useGetProducts(
    GetProductsRequestType.ONE_TIME_PRODUCT,
    customer.default_country
  );

  const [currentStep, setCurrentStep] = useState(1);

  const petOptions = orderPets.map((pet: Pet) => {
    const hasFresh = hasFreshProduct(pet?.pet_plan?.products || []);
    const hasBuffer = petPlanHasBufferBags(order.products || [], pet.pet_plan?.id || '');
    return {
      value: pet.id,
      label: hasFresh
        ? `${pet.name} (meal type not eligible)`
        : hasBuffer
        ? `${pet.name} (remove previously added extra meals to proceed)`
        : pet.name,
      isDisabled: hasFresh || hasBuffer,
    };
  });

  const isEverythingSelected = useMemo(() => !!selectedDog?.label, [selectedDog?.label]);

  const navigateBetweenSteps = () => setCurrentStep(isEverythingSelected ? 2 : 1);

  const context = {
    customer,
    order,
    petOptions,
    fullProducts: products,
    selectedDog,
    setSelectedDog,
    selectedProducts,
    setSelectedProducts,
    isLoading,
    navigateBetweenSteps,
    onConfirm,
    scheduledDate: mapSntApiDate(order.scheduled),
  };

  return (
    <div className="fixed inset-0 flex items-center justify-center z-50 modal">
      <div className="bg-white rounded-lg shadow-md p-4 min-w-[700px] relative border">
        <Close className="absolute top-1 right-1 w-8 h-8 cursor-pointer" onClick={onCancel} />
        <h1 className="font-bold text-left mb-2">Add Extra Meals</h1>
        <LineInfo
          title="Customer"
          value={`${getCountryFlag(customer.default_country)} ${generateFullName(
            customer.first_name,
            customer.last_name
          )} (${customer.email})`}
        />
        <LineInfo title="Order Date" value={mapSntApiDate(order.scheduled)} />
        <hr />
        <BufferBagsContext.Provider value={context}>
          {currentStep === 1 ? <BufferBagsStep1 /> : <BufferBagsStep2 />}
        </BufferBagsContext.Provider>
      </div>
    </div>
  );
};
